import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Checklist as ChecklistIcon,
  FileDownload as FileDownloadIcon,
  InfoOutlined as InfoOutlinedIcon,
  PictureAsPdf as PictureAsPdfIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Search as SearchIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import ProgramacionParametrosDialog from './ProgramacionParametrosDialog';
import PDFViewer from '../../components/shared/PDFViewer';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import SearchableSelect from '../../components/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import { catalogosService } from '../../services/catalogosService';
import ubicacionService, { etiquetaUbicacionSeleccion } from '../../services/ubicacionService';
import produccionProgramacionService, {
  PrioridadProgramacion,
  ProduccionProgramacionEstado,
  ProduccionProgramacionInsumo,
  ProduccionProgramacionItem,
} from '../../services/produccionProgramacionService';
import { getApiErrorMessage } from '../../utils/apiError';

const VENTANAS = [
  { value: '30', label: '30 días' },
  { value: '60', label: '60 días' },
  { value: '90', label: '90 días' },
  { value: '180', label: '180 días' },
];

const VENTANAS_VALIDAS = [30, 60, 90, 180];
const PRIORIDADES_VALIDAS: PrioridadProgramacion[] = ['todos', 'critico', 'aproducir', 'sobrestock', 'sinmovimiento'];
const FILAS_VALIDAS = [10, 25, 50, 100];
const PREFERENCIAS_KEY = 'eone.produccion.programacion.preferencias';

type PreferenciasProgramacion = {
  codigoRamo: string;
  codigoLinea: string;
  codigoClase: string;
  nroUbicacion: string;
  ventanaDias: number;
  horizonteDias: number;
  ajusteDemandaPct: number;
  searchTerm: string;
  prioridad: PrioridadProgramacion;
  rowsPerPage: number;
};

const preferenciasPorDefecto = (): PreferenciasProgramacion => ({
  codigoRamo: '',
  codigoLinea: '',
  codigoClase: '',
  nroUbicacion: '',
  ventanaDias: 30,
  horizonteDias: 30,
  ajusteDemandaPct: 0,
  searchTerm: '',
  prioridad: 'aproducir',
  rowsPerPage: 25,
});

const acotar = (valor: number, min: number, max: number, defecto: number) =>
  Number.isFinite(valor) && valor >= min && valor <= max ? valor : defecto;

const clavePreferencias = (username: string, tenantId: number | null) => {
  const identidad = `${tenantId ?? 0}_${username.trim().toLowerCase()}`.replace(/[^a-z0-9_-]/g, '_');
  return `${PREFERENCIAS_KEY}.${identidad}`;
};

const leerCookie = (nombre: string) => {
  const prefijo = `${nombre}=`;
  const hallada = document.cookie.split('; ').find((parte) => parte.startsWith(prefijo));
  return hallada ? decodeURIComponent(hallada.slice(prefijo.length)) : null;
};

const guardarCookie = (nombre: string, valor: string) => {
  const unAnio = 60 * 60 * 24 * 365;
  document.cookie = `${nombre}=${encodeURIComponent(valor)}; Path=/; Max-Age=${unAnio}; SameSite=Lax`;
};

const leerPreferencias = (username: string, tenantId: number | null): PreferenciasProgramacion => {
  const defecto = preferenciasPorDefecto();
  if (!username.trim() || typeof window === 'undefined') return defecto;
  try {
    const raw = leerCookie(clavePreferencias(username, tenantId));
    if (!raw) return defecto;
    const data = JSON.parse(raw) as Partial<PreferenciasProgramacion>;
    const prioridad = PRIORIDADES_VALIDAS.includes(data.prioridad as PrioridadProgramacion)
      ? (data.prioridad as PrioridadProgramacion)
      : defecto.prioridad;
    return {
      codigoRamo: typeof data.codigoRamo === 'string' ? data.codigoRamo : defecto.codigoRamo,
      codigoLinea: typeof data.codigoLinea === 'string' ? data.codigoLinea : defecto.codigoLinea,
      codigoClase: typeof data.codigoClase === 'string' ? data.codigoClase : defecto.codigoClase,
      nroUbicacion: typeof data.nroUbicacion === 'string' ? data.nroUbicacion : defecto.nroUbicacion,
      ventanaDias: VENTANAS_VALIDAS.includes(Number(data.ventanaDias)) ? Number(data.ventanaDias) : defecto.ventanaDias,
      horizonteDias: acotar(Number(data.horizonteDias), 1, 365, defecto.horizonteDias),
      ajusteDemandaPct: acotar(Number(data.ajusteDemandaPct), -100, 500, defecto.ajusteDemandaPct),
      searchTerm: typeof data.searchTerm === 'string' ? data.searchTerm : defecto.searchTerm,
      prioridad,
      rowsPerPage: FILAS_VALIDAS.includes(Number(data.rowsPerPage)) ? Number(data.rowsPerPage) : defecto.rowsPerPage,
    };
  } catch {
    return defecto;
  }
};

const guardarPreferencias = (username: string, tenantId: number | null, prefs: PreferenciasProgramacion) => {
  if (!username.trim() || typeof window === 'undefined') return;
  try {
    guardarCookie(clavePreferencias(username, tenantId), JSON.stringify(prefs));
  } catch {
    /* el navegador puede rechazar la escritura si el almacenamiento está lleno */
  }
};

const etiquetaFiltroGrilla = (id: string) => {
  switch (id) {
    case 'aproducir': return 'A producir';
    case 'critico': return 'Críticos';
    case 'sobrestock': return 'Sobrestock';
    case 'sinmovimiento': return 'Sin movimiento';
    default: return 'Todos';
  }
};

const etiquetaPrioridad = (prioridad: string) => {
  switch (prioridad) {
    case 'Critico': return 'Crítico';
    case 'Producir': return 'Producir';
    case 'Sobrestock': return 'Sobrestock';
    case 'SinMovimiento': return 'Sin movimiento';
    default: return 'Normal';
  }
};

const colorPrioridad = (prioridad: string): 'error' | 'warning' | 'success' | 'default' | 'info' => {
  switch (prioridad) {
    case 'Critico': return 'error';
    case 'Producir': return 'warning';
    case 'Sobrestock': return 'info';
    case 'SinMovimiento': return 'default';
    default: return 'success';
  }
};

const fmt = (n: number, digits = 2) =>
  Number(n ?? 0).toLocaleString('es-AR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtFecha = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-AR');
};

type ColumnaGrilla = {
  id: string;
  label: string;
  align?: 'right';
  ayuda: string;
};

const COLUMNAS: ColumnaGrilla[] = [
  {
    id: 'prioridad',
    label: 'Prioridad',
    ayuda: 'Clasifica la urgencia de fabricar. Crítico: no hay oferta y hay demanda, o la cobertura no alcanza el horizonte. Producir: hay cantidad sugerida. Normal: la oferta alcanza el horizonte. Sobrestock: la oferta supera el stock máximo y no se sugiere fabricar. Sin movimiento: no hubo ventas netas en la ventana y no hay faltante. Pase el mouse sobre la etiqueta de la fila para ver el detalle de ese artículo.',
  },
  {
    id: 'producto',
    label: 'Producto',
    ayuda: 'Código y descripción del artículo con receta. El botón de al lado abre el lote, el lead time, la cobertura y el stock de seguridad de ese artículo. Si la receta no tiene una cantidad de lote válida, la fila lo indica.',
  },
  {
    id: 'insumos',
    label: 'Insumos',
    ayuda: 'Estado de los componentes para la cantidad sugerida. El stock se reparte primero a los más críticos, así dos productos no ven el mismo saldo completo. Semielaborado significa que el componente tiene receta propia y puede aparecer como otra línea.',
  },
  {
    id: 'stock',
    label: 'Stock',
    align: 'right',
    ayuda: 'Existencia actual. Sin depósito, es el stock de toda la empresa. Si eligió un depósito antes de calcular, es la cantidad de esa ubicación. Un valor negativo se muestra en rojo y aumenta lo que hace falta fabricar.',
  },
  {
    id: 'reservado',
    label: 'Reservado',
    align: 'right',
    ayuda: 'Cantidad ya comprometida en pedidos. Se resta del stock. Sin depósito, es la reserva de la empresa. Con depósito, es la cantidad asignada de esa ubicación y puede no coincidir con el detalle de cada pedido.',
  },
  {
    id: 'enProduccion',
    label: 'En producción',
    align: 'right',
    ayuda: 'Cantidad de las órdenes que ya se iniciaron y todavía no se confirmaron. No forma parte del stock disponible. Es un saldo de la empresa: no cambia aunque calcule por un depósito.',
  },
  {
    id: 'programado',
    label: 'Programado',
    align: 'right',
    ayuda: 'Suma de las cantidades de las órdenes en borrador. Todavía no están en producción. Se cuentan para no volver a sugerir la misma cantidad.',
  },
  {
    id: 'oferta',
    label: 'Oferta',
    align: 'right',
    ayuda: 'Lo que ya está cubierto: stock menos reservado, más lo que está en producción, más lo programado en borrador. La cantidad sugerida se calcula a partir de esta cifra.',
  },
  {
    id: 'ventas',
    label: 'Ventas',
    align: 'right',
    ayuda: 'Ventas netas de la ventana elegida al calcular (30, 60, 90 o 180 días corridos hasta hoy, incluido). Las facturas suman y las notas de crédito restan. No usa los 6 meses cerrados de Sugerencia de compra.',
  },
  {
    id: 'demanda',
    label: 'Demanda diaria',
    align: 'right',
    ayuda: 'Ventas netas de la ventana divididas por la cantidad de días de esa ventana, y luego ajustadas con el porcentaje de Ajuste demanda. El divisor es toda la ventana, también los días sin ventas. Nunca queda en negativo.',
  },
  {
    id: 'cobertura',
    label: 'Cobertura',
    align: 'right',
    ayuda: 'Días que dura la oferta al ritmo de la demanda diaria. Si no hay demanda y hay oferta, se muestra un guion: el stock no se agota con las ventas del período. Si no hay demanda ni oferta, es cero.',
  },
  {
    id: 'minimo',
    label: 'Mínimo',
    align: 'right',
    ayuda: 'Stock mínimo del artículo. Si es mayor a cero, se suma a lo que hay que cubrir, como reserva de seguridad. Si es cero, no se agrega un mínimo.',
  },
  {
    id: 'sugerida',
    label: 'Sugerida',
    align: 'right',
    ayuda: 'Cantidad a fabricar para cubrir la demanda del horizonte más el mínimo, después de descontar la oferta. Se redondea hacia arriba. Si el artículo tiene stock máximo, la sugerencia no lo supera.',
  },
  {
    id: 'cantidad',
    label: 'Cantidad',
    align: 'right',
    ayuda: 'Puede cambiar este número para el Excel y el PDF. El cambio queda solo en esta pantalla: no modifica el cálculo guardado ni crea una orden de producción.',
  },
  {
    id: 'fecha',
    label: 'Fecha',
    ayuda: 'Día sugerido para fabricar. Si la cobertura no llega al horizonte, es hoy. Si la oferta dura más que el horizonte, es hoy más los días de cobertura que sobran.',
  },
];

const PREVIEW_STORAGE_KEY = 'eone.produccion.programacion.preview';

type SeleccionLinea = { descripcion: string; cantidad: number; fechaSugerida: string };

const etiquetaInsumos = (estado?: string) => {
  switch (estado) {
    case 'Cubierto': return 'Cubierto';
    case 'FaltanteParcial': return 'Faltante parcial';
    case 'SinStock': return 'Sin stock';
    case 'RecetaInvalida': return 'Receta inválida';
    default: return '';
  }
};

const colorInsumos = (estado?: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (estado) {
    case 'Cubierto': return 'success';
    case 'FaltanteParcial': return 'warning';
    case 'SinStock':
    case 'RecetaInvalida': return 'error';
    default: return 'default';
  }
};

const ProgramacionProduccion: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, tenantId } = useAuth();
  const username = user?.username ?? '';
  const [ramos, setRamos] = useState<{ value: string; label: string }[]>([]);
  const [lineas, setLineas] = useState<{ value: string; label: string }[]>([]);
  const [clases, setClases] = useState<{ value: string; label: string }[]>([]);
  const [depositos, setDepositos] = useState<{ value: string; label: string }[]>([]);

  const [codigoRamo, setCodigoRamo] = useState('');
  const [codigoLinea, setCodigoLinea] = useState('');
  const [codigoClase, setCodigoClase] = useState('');
  const [nroUbicacion, setNroUbicacion] = useState('');
  const [ventanaDias, setVentanaDias] = useState(30);
  const [horizonteDias, setHorizonteDias] = useState(30);
  const [ajusteDemandaPct, setAjusteDemandaPct] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [prioridad, setPrioridad] = useState<PrioridadProgramacion>('aproducir');

  const [estado, setEstado] = useState<ProduccionProgramacionEstado | null>(null);
  const [items, setItems] = useState<ProduccionProgramacionItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [kpis, setKpis] = useState({ itemsCriticos: 0, itemsAProducir: 0, unidadesSugeridas: 0, coberturaMediana: 0 });
  const [cargando, setCargando] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [parametrosArticulo, setParametrosArticulo] = useState<{ codigo: string; descripcion: string } | null>(null);
  const previewPdfUrlRef = useRef<string | null>(null);
  previewPdfUrlRef.current = previewPdfUrl;
  const [ediciones, setEdiciones] = useState<Record<string, number>>({});
  const [seleccion, setSeleccion] = useState<Record<string, SeleccionLinea>>({});
  const [seleccionando, setSeleccionando] = useState(false);
  const [insumosDetalle, setInsumosDetalle] = useState<{ codigo: string; descripcion: string; insumos: ProduccionProgramacionInsumo[] } | null>(null);
  const [ayudaColumna, setAyudaColumna] = useState<{ anchor: HTMLElement; columna: ColumnaGrilla } | null>(null);
  const [preferenciasListas, setPreferenciasListas] = useState(false);
  const [identidadAplicada, setIdentidadAplicada] = useState('');
  const identidad = `${tenantId ?? 0}:${username.trim().toLowerCase()}`;

  useEffect(() => {
    const prefs = leerPreferencias(username, tenantId);
    setCodigoRamo(prefs.codigoRamo);
    setCodigoLinea(prefs.codigoLinea);
    setCodigoClase(prefs.codigoClase);
    setNroUbicacion(prefs.nroUbicacion);
    setVentanaDias(prefs.ventanaDias);
    setHorizonteDias(prefs.horizonteDias);
    setAjusteDemandaPct(prefs.ajusteDemandaPct);
    setSearchTerm(prefs.searchTerm);
    setPrioridad(prefs.prioridad);
    setRowsPerPage(prefs.rowsPerPage);
    setPage(0);
    setIdentidadAplicada(identidad);
    setPreferenciasListas(true);
  }, [identidad, tenantId, username]);

  useEffect(() => {
    if (!preferenciasListas || identidadAplicada !== identidad) return;
    guardarPreferencias(username, tenantId, {
      codigoRamo,
      codigoLinea,
      codigoClase,
      nroUbicacion,
      ventanaDias,
      horizonteDias,
      ajusteDemandaPct,
      searchTerm,
      prioridad,
      rowsPerPage,
    });
  }, [
    ajusteDemandaPct,
    codigoClase,
    codigoLinea,
    codigoRamo,
    horizonteDias,
    identidad,
    identidadAplicada,
    nroUbicacion,
    preferenciasListas,
    prioridad,
    rowsPerPage,
    searchTerm,
    tenantId,
    username,
    ventanaDias,
  ]);

  useEffect(() => {
    return () => {
      if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
    };
  }, []);

  useEffect(() => {
    catalogosService.getRamos(1, 1000, '').then((r) => {
      setRamos((r.items ?? []).map((x) => ({ value: x.codigoRamo, label: `${x.codigoRamo} - ${x.nombreRamo}` })));
    }).catch(() => setRamos([]));
    catalogosService.getLineas(1, 1000, '').then((r) => {
      setLineas((r.items ?? []).map((x) => ({ value: x.codigoLinea, label: `${x.codigoLinea} - ${x.nombreLinea}` })));
    }).catch(() => setLineas([]));
    catalogosService.getClases(1, 1000, '').then((r) => {
      setClases((r.items ?? []).map((x) => ({ value: x.codigoClase, label: `${x.codigoClase} - ${x.nombreClase}` })));
    }).catch(() => setClases([]));
    ubicacionService.getUbicaciones({ pageNumber: 1, pageSize: 200 }).then((rows) => {
      setDepositos(rows.map((u) => ({ value: String(u.nroUbicacion), label: etiquetaUbicacionSeleccion(u) })));
    }).catch(() => setDepositos([]));
  }, []);

  const snapshotReady = estado?.status === 'ready' || estado?.isRecalculating === true;

  const cargar = useCallback(async (pageNumber: number, pageSize: number) => {
    try {
      setCargando(true);
      const data = await produccionProgramacionService.getSugerencias({
        codigoRamo,
        codigoLinea,
        codigoClase,
        prioridad,
        searchTerm,
        pageNumber,
        pageSize,
      });
      setItems(data.items ?? []);
      setTotalCount(data.totalCount ?? 0);
      setKpis(data.kpis ?? { itemsCriticos: 0, itemsAProducir: 0, unidadesSugeridas: 0, coberturaMediana: 0 });
      setEstado(data.snapshot);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo leer la programación.'), { variant: 'error' });
    } finally {
      setCargando(false);
    }
  }, [codigoClase, codigoLinea, codigoRamo, enqueueSnackbar, prioridad, searchTerm]);

  useEffect(() => {
    if (!preferenciasListas) return;
    produccionProgramacionService.getEstado().then((e) => {
      setEstado(e);
      if (e.status === 'ready' || e.isRecalculating) {
        void cargar(1, rowsPerPage);
      }
    }).catch(() => setEstado({ status: 'none', isRecalculating: false }));
  }, [cargar, preferenciasListas, rowsPerPage]);

  const handleCalcular = async () => {
    try {
      setCalculando(true);
      const e = await produccionProgramacionService.calcular({
        codigoRamo,
        codigoLinea,
        codigoClase,
        nroUbicacion: nroUbicacion ? Number(nroUbicacion) : undefined,
        ventanaDias,
        horizonteDias,
        ajusteDemandaPct,
      });
      setEstado(e);
      setEdiciones({});
      setPage(0);
      await cargar(1, rowsPerPage);
      enqueueSnackbar('Programación calculada.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo calcular la programación.'), { variant: 'error' });
      const actual = await produccionProgramacionService.getEstado().catch(() => null);
      if (actual) setEstado(actual);
    } finally {
      setCalculando(false);
    }
  };

  const fetchAll = useCallback(async () => {
    const all: ProduccionProgramacionItem[] = [];
    let pageNumber = 1;
    let totalPages = 1;
    do {
      const resp = await produccionProgramacionService.getSugerencias({
        codigoRamo,
        codigoLinea,
        codigoClase,
        prioridad,
        searchTerm,
        pageNumber,
        pageSize: 200,
      });
      all.push(...(resp.items ?? []));
      totalPages = resp.totalPages || 1;
      pageNumber += 1;
    } while (pageNumber <= totalPages);
    return all;
  }, [codigoClase, codigoLinea, codigoRamo, prioridad, searchTerm]);

  const cantidadDe = (row: ProduccionProgramacionItem) => ediciones[row.codigoArticulo] ?? row.cantidadSugerida;

  const toggleSeleccion = (row: ProduccionProgramacionItem) => {
    setSeleccion((prev) => {
      const next = { ...prev };
      if (next[row.codigoArticulo]) delete next[row.codigoArticulo];
      else {
        next[row.codigoArticulo] = {
          descripcion: row.descripcion,
          cantidad: cantidadDe(row),
          fechaSugerida: row.fechaSugerida,
        };
      }
      return next;
    });
  };

  const togglePagina = () => {
    const visibles = items.filter((row) => cantidadDe(row) > 0);
    const todas = visibles.length > 0 && visibles.every((row) => Boolean(seleccion[row.codigoArticulo]));
    setSeleccion((prev) => {
      const next = { ...prev };
      for (const row of visibles) {
        if (todas) delete next[row.codigoArticulo];
        else next[row.codigoArticulo] = { descripcion: row.descripcion, cantidad: cantidadDe(row), fechaSugerida: row.fechaSugerida };
      }
      return next;
    });
  };

  const handleSeleccionarSugeridos = async () => {
    if (!snapshotReady) {
      enqueueSnackbar('Primero calcule la programación.', { variant: 'warning' });
      return;
    }
    try {
      setSeleccionando(true);
      const all = await fetchAll();
      const next: Record<string, SeleccionLinea> = {};
      for (const row of all) {
        const cantidad = ediciones[row.codigoArticulo] ?? row.cantidadSugerida;
        if (cantidad > 0 && row.recetaValida) {
          next[row.codigoArticulo] = { descripcion: row.descripcion, cantidad, fechaSugerida: row.fechaSugerida };
        }
      }
      setSeleccion(next);
      enqueueSnackbar(
        Object.keys(next).length === 0
          ? 'No hay filas sugeridas con cantidad mayor a cero.'
          : `Se seleccionaron ${Object.keys(next).length} artículos.`,
        { variant: Object.keys(next).length === 0 ? 'warning' : 'success' },
      );
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron seleccionar las sugerencias.'), { variant: 'error' });
    } finally {
      setSeleccionando(false);
    }
  };

  const handleGenerarOrdenes = () => {
    const lineas = Object.entries(seleccion)
      .map(([codigoArticulo, linea]) => ({
        codigoArticulo,
        descripcion: linea.descripcion,
        cantidad: ediciones[codigoArticulo] ?? linea.cantidad,
        fechaSugerida: linea.fechaSugerida,
      }))
      .filter((linea) => linea.cantidad > 0);
    if (lineas.length === 0) {
      enqueueSnackbar('Seleccione al menos un artículo con cantidad mayor a cero.', { variant: 'warning' });
      return;
    }
    sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(lineas));
    navigate('/produccion/programacion/preview', { state: { lineas } });
  };

  const handleExport = async () => {
    if (!snapshotReady) {
      enqueueSnackbar('Primero debe calcular la programación.', { variant: 'warning' });
      return;
    }
    try {
      setExportando(true);
      const rows = await fetchAll();
      if (rows.length === 0) {
        enqueueSnackbar('No hay filas para exportar.', { variant: 'info' });
        return;
      }
      const exportRows = rows.map((r) => ({
        Prioridad: etiquetaPrioridad(r.prioridad),
        Código: r.codigoArticulo,
        Descripción: r.descripcion,
        Ramo: r.codigoRamo,
        Línea: r.codigoLinea,
        Clase: r.codigoClase,
        Stock: r.stockActual,
        Reservado: r.reservado,
        'En producción': r.produccionEnCurso,
        Programado: r.programado,
        Oferta: r.oferta,
        'Ventas del período': r.ventasNetas,
        'Demanda diaria': r.demandaDiaria,
        'Cobertura (días)': r.coberturaDias,
        'Stock mínimo': r.stockMin,
        'Cantidad sugerida': r.cantidadSugerida,
        'Cantidad editable': ediciones[r.codigoArticulo] ?? r.cantidadSugerida,
        'Fecha sugerida': fmtFecha(r.fechaSugerida),
        'Stock negativo': r.stockNegativo ? 'Sí' : 'No',
        Consejo: r.consejo,
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(wb, ws, 'Programacion');
      XLSX.writeFile(wb, `ProgramacionProduccion_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo exportar.'), { variant: 'error' });
    } finally {
      setExportando(false);
    }
  };

  const cerrarPdf = useCallback(() => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleImprimirPdf = async () => {
    if (!snapshotReady) {
      enqueueSnackbar('Primero debe calcular la programación.', { variant: 'warning' });
      return;
    }
    try {
      setGenerandoPdf(true);
      const rows = await fetchAll();
      if (rows.length === 0) {
        enqueueSnackbar('No hay filas para imprimir.', { variant: 'info' });
        return;
      }
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 28;
      let y = 36;
      const calculo = estado?.filtrosCalculo;
      const ventana = calculo?.ventanaDias ?? ventanaDias;
      const horizonte = calculo?.horizonteDias ?? horizonteDias;
      const ajuste = calculo?.ajusteDemandaPct ?? ajusteDemandaPct;
      const deposito = nroUbicacion
        ? (depositos.find((d) => d.value === nroUbicacion)?.label ?? `Depósito ${nroUbicacion}`)
        : 'Consolidado';
      const nombreFiltro = (codigo: string, opciones: { value: string; label: string }[], vacio: string) =>
        codigo ? (opciones.find((o) => o.value === codigo)?.label ?? codigo) : vacio;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(25, 118, 210);
      doc.text('Programación de producción', marginX, y);
      y += 16;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(60);
      const anchoTexto = doc.internal.pageSize.getWidth() - marginX * 2;
      const cabecera = [
        `Ventana: ${ventana} días   Horizonte: ${horizonte} días   Ajuste: ${fmt(ajuste, 0)}%   Stock: ${deposito}`,
        `Listado: ${etiquetaFiltroGrilla(prioridad)}   Ramo: ${nombreFiltro(codigoRamo, ramos, 'Todos')}   Línea: ${nombreFiltro(codigoLinea, lineas, 'Todas')}   Clase: ${nombreFiltro(codigoClase, clases, 'Todas')}${searchTerm.trim() ? `   Búsqueda: ${searchTerm.trim()}` : ''}`,
        `Críticos: ${kpis.itemsCriticos}   A producir: ${kpis.itemsAProducir}   Unidades sugeridas: ${fmt(kpis.unidadesSugeridas, 0)}   Cobertura mediana: ${fmt(kpis.coberturaMediana, 0)} días   Filas: ${rows.length}   Generado: ${new Date().toLocaleString('es-AR')}`,
      ];
      cabecera.forEach((linea) => {
        const trozos = doc.splitTextToSize(linea, anchoTexto) as string[];
        doc.text(trozos, marginX, y);
        y += 12 * trozos.length;
      });
      y += 4;

      const totalCantidad = rows.reduce(
        (acum, row) => acum + Number(ediciones[row.codigoArticulo] ?? row.cantidadSugerida ?? 0),
        0,
      );

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [[
          'Prioridad',
          'Código',
          'Descripción',
          'Stock',
          'Reserv.',
          'En prod.',
          'Prog.',
          'Oferta',
          'Ventas',
          'Dem./día',
          'Cob.',
          'Mín.',
          'Sugerida',
          'Cantidad',
          'Fecha',
        ]],
        body: rows.map((row) => [
          etiquetaPrioridad(row.prioridad),
          row.codigoArticulo,
          row.descripcion,
          fmt(row.stockActual, 0),
          fmt(row.reservado, 0),
          fmt(row.produccionEnCurso, 0),
          fmt(row.programado, 0),
          fmt(row.oferta, 0),
          fmt(row.ventasNetas, 0),
          fmt(row.demandaDiaria, 2),
          row.coberturaDias >= 9999 ? '—' : fmt(row.coberturaDias, 0),
          fmt(row.stockMin, 0),
          fmt(row.cantidadSugerida, 0),
          fmt(ediciones[row.codigoArticulo] ?? row.cantidadSugerida, 0),
          fmtFecha(row.fechaSugerida),
        ]),
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          7: { halign: 'right' },
          8: { halign: 'right' },
          9: { halign: 'right' },
          10: { halign: 'right' },
          11: { halign: 'right' },
          12: { halign: 'right' },
          13: { halign: 'right' },
        },
        foot: [[
          '',
          '',
          'TOTAL',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          fmt(totalCantidad, 0),
          '',
        ]],
        showFoot: 'lastPage',
        footStyles: { fillColor: [245, 245, 245], textColor: 20, fontStyle: 'bold' },
      });

      const pdfBlob = doc.output('blob');
      const safeBlob = pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf'
        ? pdfBlob
        : new Blob([pdfBlob], { type: 'application/pdf' });
      const url = URL.createObjectURL(safeBlob);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo generar el PDF.'), { variant: 'error' });
    } finally {
      setGenerandoPdf(false);
    }
  };

  const chips = useMemo(() => ([
    { id: 'aproducir' as const, label: 'A producir' },
    { id: 'critico' as const, label: 'Críticos' },
    { id: 'sobrestock' as const, label: 'Sobrestock' },
    { id: 'sinmovimiento' as const, label: 'Sin movimiento' },
    { id: 'todos' as const, label: 'Todos' },
  ]), []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Programación de producción
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sugiere qué fabricar según stock, producción en curso, borradores y el promedio de ventas de la ventana elegida.
                Si hay ventas del mismo mes del año pasado, el cálculo las usa como factor estacional (entre 0,5 y 2).
                Esa ventana es de días corridos y no coincide con los 6 meses cerrados de Sugerencia de compra.
              </Typography>
            </Box>

            {estado?.status === 'processing' && !estado.isRecalculating && (
              <Alert severity="info">Procesando la programación. Puede tardar varios minutos.</Alert>
            )}
            {estado?.status === 'error' && (
              <Alert severity="error">{estado.errorMessage || 'El último cálculo falló.'}</Alert>
            )}
            {estado?.status === 'none' && (
              <Alert severity="info">No hay un cálculo generado. Configure los filtros y presione Calcular.</Alert>
            )}
            {nroUbicacion && (
              <Alert severity="warning">
                El stock y la reserva salen del depósito elegido. La producción en curso es de la empresa: no hay saldo de WIP por depósito.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect label="Ramo" allowEmpty emptyOptionLabel="Todos" value={codigoRamo || null} onChange={(v) => { setCodigoRamo(v ?? ''); setPage(0); }} options={ramos} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect label="Línea" allowEmpty emptyOptionLabel="Todas" value={codigoLinea || null} onChange={(v) => { setCodigoLinea(v ?? ''); setPage(0); }} options={lineas} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect label="Clase" allowEmpty emptyOptionLabel="Todas" value={codigoClase || null} onChange={(v) => { setCodigoClase(v ?? ''); setPage(0); }} options={clases} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SearchableSelect label="Depósito" allowEmpty emptyOptionLabel="Consolidado" value={nroUbicacion || null} onChange={(v) => setNroUbicacion(v ?? '')} options={depositos} />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <SearchableSelect label="Ventana" value={String(ventanaDias)} onChange={(v) => setVentanaDias(Number(v ?? 30))} options={VENTANAS} />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Horizonte"
                  value={horizonteDias}
                  onChange={(e) => setHorizonteDias(Number(e.target.value) || 0)}
                  inputProps={{ min: 1, max: 365 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">días</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Ajuste demanda"
                  value={ajusteDemandaPct}
                  onChange={(e) => setAjusteDemandaPct(Number(e.target.value) || 0)}
                  inputProps={{ min: -100, max: 500 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  helperText="Se aplica al recalcular."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', lg: 'center' }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {chips.map((chip) => (
                  <Chip
                    key={chip.id}
                    label={chip.label}
                    color={prioridad === chip.id ? 'primary' : 'default'}
                    onClick={() => { setPrioridad(chip.id); setPage(0); }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={exportando ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                  onClick={() => void handleExport()}
                  disabled={exportando || generandoPdf || calculando}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={generandoPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                  onClick={() => void handleImprimirPdf()}
                  disabled={exportando || generandoPdf || calculando}
                >
                  {generandoPdf ? 'Generando PDF…' : 'Imprimir PDF'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={seleccionando ? <CircularProgress size={16} /> : <ChecklistIcon />}
                  onClick={() => void handleSeleccionarSugeridos()}
                  disabled={seleccionando || calculando || !snapshotReady}
                >
                  Seleccionar sugeridos
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PrecisionManufacturingIcon />}
                  onClick={handleGenerarOrdenes}
                  disabled={calculando || Object.keys(seleccion).length === 0}
                >
                  {`Generar órdenes (${Object.keys(seleccion).length})`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={calculando ? <CircularProgress size={16} color="inherit" /> : <CalculateIcon />}
                  onClick={() => void handleCalcular()}
                  disabled={calculando}
                >
                  {calculando ? 'Calculando…' : 'Calcular'}
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              {[
                ['Críticos', String(kpis.itemsCriticos)],
                ['A producir', String(kpis.itemsAProducir)],
                ['Unidades sugeridas', fmt(kpis.unidadesSugeridas, 0)],
                ['Cobertura mediana', `${fmt(kpis.coberturaMediana, 0)} días`],
              ].map(([label, value]) => (
                <Grid item xs={6} md={3} key={label}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="h6">{value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={items.some((row) => cantidadDe(row) > 0) && items.filter((row) => cantidadDe(row) > 0).every((row) => Boolean(seleccion[row.codigoArticulo]))}
                        indeterminate={items.some((row) => Boolean(seleccion[row.codigoArticulo])) && !items.filter((row) => cantidadDe(row) > 0).every((row) => Boolean(seleccion[row.codigoArticulo]))}
                        onChange={togglePagina}
                        inputProps={{ 'aria-label': 'Seleccionar filas de la página' }}
                      />
                    </TableCell>
                    {COLUMNAS.map((columna) => (
                      <TableCell key={columna.id} align={columna.align}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: columna.align === 'right' ? 'flex-end' : 'flex-start',
                            gap: 0.25,
                            width: '100%',
                          }}
                        >
                          {columna.label}
                          <IconButton
                            size="small"
                            aria-label={`Información de ${columna.label}`}
                            onClick={(event) => setAyudaColumna({ anchor: event.currentTarget, columna })}
                            sx={{ p: 0.25 }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 15 }} color="info" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cargando && (
                    <TableRow>
                      <TableCell colSpan={COLUMNAS.length + 1} align="center"><CircularProgress size={22} /></TableCell>
                    </TableRow>
                  )}
                  {!cargando && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={COLUMNAS.length + 1}>
                        {snapshotReady ? 'Sin resultados para los filtros aplicados.' : 'Calcule la programación para ver resultados.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {!cargando && items.map((row) => (
                    <TableRow key={row.codigoArticulo} hover selected={Boolean(seleccion[row.codigoArticulo])}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={Boolean(seleccion[row.codigoArticulo])}
                          onChange={() => toggleSeleccion(row)}
                          inputProps={{ 'aria-label': `Seleccionar ${row.codigoArticulo}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={row.consejo}>
                          <Chip size="small" label={etiquetaPrioridad(row.prioridad)} color={colorPrioridad(row.prioridad)} />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{row.codigoArticulo}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.descripcion}</Typography>
                          </Box>
                          <IconButton
                            size="small"
                            aria-label={`Parámetros de ${row.codigoArticulo}`}
                            onClick={() => setParametrosArticulo({ codigo: row.codigoArticulo, descripcion: row.descripcion })}
                            sx={{ p: 0.25 }}
                          >
                            <TuneIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                        {!row.recetaValida && (
                          <Typography variant="caption" color="error" display="block">Receta sin batch válido</Typography>
                        )}
                        {row.lineaSemielaborado && (
                          <Typography variant="caption" color="text.secondary" display="block">Semielaborado</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {etiquetaInsumos(row.estadoInsumos) ? (
                          <Chip
                            size="small"
                            label={etiquetaInsumos(row.estadoInsumos)}
                            color={colorInsumos(row.estadoInsumos)}
                            onClick={() => setInsumosDetalle({ codigo: row.codigoArticulo, descripcion: row.descripcion, insumos: row.insumos ?? [] })}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                        {(row.cantidadFaltante ?? 0) > 0 && (
                          <Typography variant="caption" display="block">Faltan {fmt(row.cantidadFaltante ?? 0, 3)}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ color: row.stockNegativo ? 'error.main' : undefined }}>{fmt(row.stockActual, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.reservado, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.produccionEnCurso, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.programado, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.oferta, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.ventasNetas, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.demandaDiaria, 2)}</TableCell>
                      <TableCell align="right">{row.coberturaDias >= 9999 ? '—' : fmt(row.coberturaDias, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.stockMin, 0)}</TableCell>
                      <TableCell align="right">{fmt(row.cantidadSugerida, 0)}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 110 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={ediciones[row.codigoArticulo] ?? row.cantidadSugerida}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            setEdiciones((prev) => ({ ...prev, [row.codigoArticulo]: Number.isFinite(n) ? n : 0 }));
                          }}
                          inputProps={{ min: 0, style: { textAlign: 'right' } }}
                        />
                      </TableCell>
                      <TableCell>{fmtFecha(row.fechaSugerida)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Popover
              open={Boolean(ayudaColumna)}
              anchorEl={ayudaColumna?.anchor ?? null}
              onClose={() => setAyudaColumna(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ p: 2, maxWidth: 360 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {ayudaColumna?.columna.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ayudaColumna?.columna.ayuda}
                </Typography>
              </Box>
            </Popover>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, next) => {
                setPage(next);
                void cargar(next + 1, rowsPerPage);
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const size = Number(e.target.value);
                setRowsPerPage(size);
                setPage(0);
                void cargar(1, size);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Filas"
            />
          </Stack>
        </CardContent>
      </Card>
      <Dialog open={Boolean(insumosDetalle)} onClose={() => setInsumosDetalle(null)} maxWidth="md" fullWidth>
        <DialogTitle>{insumosDetalle ? `Insumos de ${insumosDetalle.codigo}` : 'Insumos'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{insumosDetalle?.descripcion}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Componente</TableCell>
                <TableCell align="right">Requerido</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="right">Asignado</TableCell>
                <TableCell align="right">Faltante</TableCell>
                <TableCell>Proveedor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(insumosDetalle?.insumos ?? []).map((insumo) => (
                <TableRow key={insumo.codigoComponente}>
                  <TableCell>
                    {insumo.codigoComponente}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {insumo.descripcion}{insumo.semielaborado ? ' · semielaborado' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{fmt(insumo.cantidadRequerida, 3)}</TableCell>
                  <TableCell align="right">{fmt(insumo.stockDisponible, 3)}</TableCell>
                  <TableCell align="right">{fmt(insumo.cantidadAsignada, 3)}</TableCell>
                  <TableCell align="right">{fmt(insumo.cantidadFaltante, 3)}</TableCell>
                  <TableCell>{insumo.codProveedor ?? '—'}</TableCell>
                </TableRow>
              ))}
              {(insumosDetalle?.insumos ?? []).length === 0 && (
                <TableRow><TableCell colSpan={6}>Sin componentes para esta cantidad.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsumosDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <ProgramacionParametrosDialog
        open={Boolean(parametrosArticulo)}
        codigo={parametrosArticulo?.codigo ?? ''}
        descripcion={parametrosArticulo?.descripcion ?? ''}
        onClose={() => setParametrosArticulo(null)}
      />
      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={cerrarPdf}
          title="Programación de producción"
          documentId="programacion-produccion"
          documentType="informeCliente"
          downloadFileName={`ProgramacionProduccion_${new Date().toISOString().slice(0, 10)}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default ProgramacionProduccion;
